/**
 * WorkspaceDetailHeader - Header section for workspace detail page
 * Contains navigation, workspace name editing, and action buttons
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import EditableWorkspaceName from '@/modules/app/components/workspace/EditableWorkspaceName';
import WorkspaceActions from '@/modules/app/components/workspace/WorkspaceActions';
import BulkExportButton, { BulkExportData } from '@/components/ui/BulkExportButton';

interface WorkspaceDetailHeaderProps {
  workspace: any;
  workspaceId: string;
  userId: string;
  bulkExportData: BulkExportData;
  hasDataForExport: boolean;
  onWorkspaceNameUpdate: (newName: string) => void;
  onWorkspaceDelete: (workspaceId: string) => void;
}

const WorkspaceDetailHeader: React.FC<WorkspaceDetailHeaderProps> = ({
  workspace,
  workspaceId,
  userId,
  bulkExportData,
  hasDataForExport,
  onWorkspaceNameUpdate,
  onWorkspaceDelete,
}) => {
  const navigate = useNavigate();

  return (
    <div className="px-6 py-6 border-b border-border/30">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
        <div className="flex items-start gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate ? navigate('/app/workspace') : window.history.back()}
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="sr-only">Back to workspaces</span>
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <EditableWorkspaceName
                workspaceId={workspaceId}
                currentName={workspace.name}
                userId={userId}
                onNameUpdate={onWorkspaceNameUpdate}
              />
             {workspace.is_default && (
               <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">
                  Default
               </span>
             )}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Created {formatDistanceToNow(new Date(workspace.created_at), { addSuffix: true })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Bulk Export Button */}
          {hasDataForExport && (
            <BulkExportButton
              data={bulkExportData}
              workspaceName={workspace.name}
              variant="ghost"
              size="sm"
            />
          )}
          <WorkspaceActions
            workspace={workspace}
            onWorkspaceUpdate={onWorkspaceNameUpdate}
            onWorkspaceDelete={onWorkspaceDelete}
            className="text-muted-foreground hover:text-foreground"
          />
        </div>
      </div>
    </div>
  );
};

export default WorkspaceDetailHeader;